<script lang="ts">
	import type { GetMemberVariablesValuesReturn, GetUserReturn } from '$lib/api/rpc';
	import Avatar from '$lib/components/Avatar/Avatar.svelte';
	import Spinner from '$lib/components/Spinner/Spinner.svelte';
	import { getHubStores } from '$lib/stores/hubStores';
	import { toastMessages } from '$lib/stores/toastMessageStore';
	import { atLeastModerator } from '$lib/utils/roles';
	import { _ } from 'svelte-i18n';
	import PvRow from './PvRow.svelte';

	export let user: {
		id: string;
		name: string;
		avatar_url: string;
	};
	type FetchUserData = {
		userData: GetUserReturn;
		memberData?: GetMemberVariablesValuesReturn;
	};

	const { api, hub } = getHubStores();

	const fetchUserData = async (): Promise<FetchUserData | undefined> => {
		try {
			const userData = await api.getUser({ hubId: $hub?.id, userId: user.id });
			if (!userData.user.memberId) {
				return { userData };
			}
			try {
				const memberData = await api.getMemberVariablesValues({
					hubId: $hub.id,
					memberId: userData.user.memberId,
				});

				return { userData, memberData };
			} catch (e) {
				toastMessages.danger($_('hub.user.profileVariable.load.error'), e);
				return { userData };
			}
		} catch (e) {
			toastMessages.danger($_('hub.user.data.load.error'), e);
			return;
		}
	};
	const userIsAtLeastModerator = (role: string | undefined): boolean => {
		return atLeastModerator({ role: role || '' });
	};
</script>

<div class="pvContainer">
	{#await fetchUserData()}
		<Spinner />
	{:then data}
		<Avatar url={user.avatar_url || ''} name={user.name} size={50} />
		<div class="dataContainer">
			<span class="cardHeader">
				<h3>{user.name}</h3>
				{#if userIsAtLeastModerator(data?.userData?.user?.role)}
					<div class="adminPill">{$_('common.admin')}</div>
				{/if}
			</span>
			<div class="dataSet" style={data?.memberData ? 'overflow-y: scroll' : 'overflow-y: hidden'}>
				{#if atLeastModerator($hub.user_context) || $hub.user_context.perms.team.view.member_ids}
					<PvRow name="MemberID" value={data?.userData.user.memberId} />
					<PvRow name="Email" value={data?.userData.user.email} />
				{/if}
				<PvRow name="Role" value={data?.userData.user.role || ''} />
				{#if data?.memberData}
					{#each data.memberData.memberVariablesValue as pv}
						<div class="pwRow">
							<PvRow name={pv.name || pv.sparq_name} value={pv.value} />
						</div>
					{/each}
				{/if}
			</div>
		</div>
	{/await}
</div>

<style>
	.pvContainer {
		display: flex;
		flex-flow: row nowrap;
		align-items: flex-start;
		justify-content: flex-start;
		block-size: 100%;
		max-block-size: 150px;
		min-inline-size: 300px;
		max-inline-size: 412px; /* 412 */
		padding: 12px; /* hardcoded for now */
	}

	.dataContainer {
		display: flex;
		flex-wrap: wrap;
	}

	.dataSet {
		display: flex;
		flex-direction: column;
		max-block-size: 120px;
		inline-size: 100%;
		overflow-y: scroll;
		word-break: keep-all;
		white-space: nowrap;
	}

	.cardHeader {
		display: flex;
	}

	.cardHeader h3 {
		inline-size: max-content;
		margin: 0;
		margin-block-end: var(--gutterSmall);
		margin-inline-end: var(--gutterSmall);
		overflow: hidden;
		font-size: 15px;
		font-weight: 100;
		text-overflow: ellipsis;
		word-break: keep-all;
		white-space: nowrap;
	}

	.adminPill {
		display: flex;
		align-items: center;
		block-size: 20px;
		padding-block: 0;
		padding-inline: var(--fontSizeSmallest);
		border-radius: 15px;
		background-color: var(--grayLight);
		color: var(--grayDarker);
		font-size: var(--fontSizeSmaller);
	}

	.pwRow:last-child {
		margin-block-end: var(--gutterSmall);
	}

	:global(.pvContainer .px-avatar .avatar) {
		margin-inline: 0 var(--gutterSmall);
	}
</style>
